import { useState, useRef, useEffect } from 'react';

export type SortField = 'name' | 'updatedAt' | 'size' | 'type';
export type SortOrder = 'asc' | 'desc';

export interface SortOption {
  field: SortField;
  order: SortOrder;
  label: string;
}

export const sortOptions: SortOption[] = [
  { field: 'name', order: 'asc', label: '이름 (가나다순)' },
  { field: 'name', order: 'desc', label: '이름 (역순)' },
  { field: 'updatedAt', order: 'desc', label: '최근 수정일' },
  { field: 'updatedAt', order: 'asc', label: '오래된 수정일' },
  { field: 'size', order: 'desc', label: '크기 (큰 순)' },
  { field: 'size', order: 'asc', label: '크기 (작은 순)' },
  { field: 'type', order: 'asc', label: '유형별' },
];

interface ToolbarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  viewMode: 'grid' | 'list';
  onViewModeChange: (mode: 'grid' | 'list') => void;
  onUploadClick: () => void;
  onNewFolderClick: () => void;
  sortField: SortField;
  sortOrder: SortOrder;
  onSortChange: (field: SortField, order: SortOrder) => void;
  selectedCount: number;
  onClearSelection: () => void;
  onDeleteSelected: () => void;
  isTrashView?: boolean;
  trashCount?: number;
  onEmptyTrash?: () => void;
  onRestoreAllTrash?: () => void;
}

export default function Toolbar({
  searchQuery,
  onSearchChange,
  viewMode,
  onViewModeChange,
  onUploadClick,
  onNewFolderClick,
  sortField,
  sortOrder,
  onSortChange,
  selectedCount,
  onClearSelection,
  onDeleteSelected,
  isTrashView = false,
  trashCount = 0,
  onEmptyTrash,
  onRestoreAllTrash,
}: ToolbarProps) {
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showEmptyTrashConfirm, setShowEmptyTrashConfirm] = useState(false);
  const sortMenuRef = useRef<HTMLDivElement>(null);
  const emptyTrashRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (sortMenuRef.current && !sortMenuRef.current.contains(e.target as Node)) {
        setShowSortMenu(false);
      }
      if (emptyTrashRef.current && !emptyTrashRef.current.contains(e.target as Node)) {
        setShowEmptyTrashConfirm(false);
      }
    };
    if (showSortMenu || showEmptyTrashConfirm) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSortMenu, showEmptyTrashConfirm]);

  const currentSortLabel = sortOptions.find(
    (o) => o.field === sortField && o.order === sortOrder
  )?.label ?? '정렬';

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 py-3">
      <div className="relative flex-1 w-full sm:max-w-md">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400">
          <i className="ri-search-line text-sm"></i>
        </span>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={isTrashView ? '휴지통 검색...' : '파일 검색...'}
          className="w-full pl-9 pr-4 py-2 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 text-foreground-800 placeholder-foreground-400 transition-all"
        />
        {searchQuery && (
          <button
            onClick={() => onSearchChange('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400 hover:text-foreground-600 cursor-pointer"
          >
            <i className="ri-close-line text-sm"></i>
          </button>
        )}
      </div>

      {isTrashView ? (
        <div className="flex items-center gap-2 flex-wrap">
          {trashCount > 0 && (
            <>
              <button
                onClick={onRestoreAllTrash}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-primary-300 text-primary-700 hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-go-back-line"></i>
                </span>
                <span className="hidden sm:inline">모두 복구</span>
              </button>
              <div className="relative" ref={emptyTrashRef}>
                <button
                  onClick={() => setShowEmptyTrashConfirm(!showEmptyTrashConfirm)}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-accent-300 text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-delete-bin-6-line"></i>
                  </span>
                  <span className="hidden sm:inline">휴지통 비우기</span>
                </button>
                {showEmptyTrashConfirm && (
                  <div className="absolute right-0 top-full mt-1 bg-background-50 border border-background-200 rounded-xl p-4 min-w-[220px] z-30 animate-in fade-in slide-in-from-top-1 duration-150">
                    <p className="text-sm text-foreground-700 mb-3">
                      휴지통의 모든 항목을 영구 삭제하시겠습니까?
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowEmptyTrashConfirm(false)}
                        className="flex-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-background-300 text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        취소
                      </button>
                      <button
                        onClick={() => {
                          onEmptyTrash?.();
                          setShowEmptyTrashConfirm(false);
                        }}
                        className="flex-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-accent-500 text-white hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        비우기
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          {selectedCount > 0 && (
            <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-2 duration-200">
              <span className="text-sm font-medium text-foreground-700 whitespace-nowrap">
                {selectedCount}개 선택됨
              </span>
              <button
                onClick={onClearSelection}
                className="px-2.5 py-1.5 text-xs font-medium rounded-md border border-background-300 text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                취소
              </button>
              <button
                onClick={onDeleteSelected}
                className="px-2.5 py-1.5 text-xs font-medium rounded-md bg-accent-500 text-white hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                삭제
              </button>
            </div>
          )}
        </>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative" ref={sortMenuRef}>
          <button
            onClick={() => setShowSortMenu(!showSortMenu)}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-background-300 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-sort-desc"></i>
            </span>
            <span className="hidden sm:inline">{currentSortLabel}</span>
          </button>
          {showSortMenu && (
            <div className="absolute right-0 top-full mt-1 bg-background-50 border border-background-200 rounded-xl py-1.5 min-w-[180px] z-30 animate-in fade-in slide-in-from-top-1 duration-150">
              {sortOptions.map((option) => (
                <button
                  key={`${option.field}-${option.order}`}
                  onClick={() => {
                    onSortChange(option.field, option.order);
                    setShowSortMenu(false);
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors cursor-pointer whitespace-nowrap text-left ${
                    sortField === option.field && sortOrder === option.order
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    {sortField === option.field && sortOrder === option.order ? (
                      <i className="ri-check-line text-primary-500"></i>
                    ) : (
                      <span className="w-4"></span>
                    )}
                  </span>
                  <span>{option.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {!isTrashView && (
          <>
            <button
              onClick={onNewFolderClick}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-background-300 text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-folder-add-line"></i>
              </span>
              <span className="hidden sm:inline">새 폴더</span>
            </button>

            <button
              onClick={onUploadClick}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-primary-500 text-white hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-upload-2-line"></i>
              </span>
              <span className="hidden sm:inline">업로드</span>
            </button>
          </>
        )}

        <div className="flex items-center bg-background-100 rounded-lg border border-background-200 p-0.5">
          <button
            onClick={() => onViewModeChange('grid')}
            className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
              viewMode === 'grid'
                ? 'bg-background-50 text-foreground-900'
                : 'text-foreground-500 hover:text-foreground-700'
            }`}
            title="그리드 보기"
          >
            <i className="ri-layout-grid-line"></i>
          </button>
          <button
            onClick={() => onViewModeChange('list')}
            className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
              viewMode === 'list'
                ? 'bg-background-50 text-foreground-900'
                : 'text-foreground-500 hover:text-foreground-700'
            }`}
            title="리스트 보기"
          >
            <i className="ri-list-check"></i>
          </button>
        </div>
      </div>
    </div>
  );
}